import React, {useState, useEffect} from 'react';
import {hooks} from '../hooks';
import {Routes} from '../routes';
import {svg} from '../assets/svg';
import {components} from '../components';
import bgFactory from '../assets/bg/factory-img.jpeg';
import { axiosInstance } from '../api/api';
import { actions } from "../store/actions";
import { TabScreens } from "../routes";

const SESSION_CREDENTIALS_KEY = 'dpp_temp_credentials';

export const SignIn: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [opacity, setOpacity] = useState<number>(0);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  useEffect(() => {
    const loadRememberedCredentials = () => {
      try {
        const rememberMeFlag = localStorage.getItem('rememberMe') === 'true';
        
        if (rememberMeFlag) {
          const saved = sessionStorage.getItem(SESSION_CREDENTIALS_KEY);
          if (saved) {
            const credentials = JSON.parse(saved);
            setName(credentials.name || '');
            setPassword(credentials.password || '');
            setRememberMe(true);
          }
        } else {
          sessionStorage.removeItem(SESSION_CREDENTIALS_KEY);
          setRememberMe(false);
        }
      } catch (error) {
        sessionStorage.removeItem(SESSION_CREDENTIALS_KEY);
      }
    };

    loadRememberedCredentials();
  }, []);

  // Auto-close error popup after 3 seconds
  useEffect(() => {
    if (errorMessage) {
      setCountdown(3);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setErrorMessage(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [errorMessage]);

  const saveCredentialsToSession = (name: string, password: string, uuid?: string) => {
    try {
      const credentials = { name, password, uuid };
      sessionStorage.setItem(SESSION_CREDENTIALS_KEY, JSON.stringify(credentials));
    } catch (error) {
      // Silent fail
    }
  };

  const clearCredentialsFromSession = () => {
    try {
      sessionStorage.removeItem(SESSION_CREDENTIALS_KEY);
    } catch (error) {
      // Silent fail
    }
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header showGoBack2={true} headerStyle={{ backgroundColor: 'transparent', margin:'10px 20px', zIndex:1 }} />;
  };

  const renderBackground = (): JSX.Element => {
    return (
      <section
        className='container'
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${bgFactory})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main className='scrollable container' style={{zIndex: 1}}>
        <section
          style={{
            backgroundColor: 'rgba(255, 255, 255)',
            padding: 20,
            height: 'auto',
            position: 'relative',
            top: '20%',
            borderRadius: 10,
          }}
        >
          <h1 style={{marginBottom: 40}}>Selamat Datang <br /> <span style={{fontWeight: '300'}}>di</span> DPP Security Patrol!</h1>
          
          <components.Input
            placeholder='Username'
            containerStyle={{marginBottom: 14}}
            leftIcon={<svg.MailSvg />}
            rightIcon={<svg.CheckSvg />}
            value = {name}
            onChange={(e) => setName(e.target.value)}
          />
          <components.Input
            placeholder='Kata Sandi'
            leftIcon={<svg.KeySvg />}
            rightIcon={<svg.EyeOffSvg />}
            onRightIconClick={() => setShowPassword(!showPassword)}
            containerStyle={{marginBottom: 20}}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          
          <div
            className='row-center-space-between'
            style={{marginBottom: 40}}
          >
            <div
              style={{gap: 10}}
              className='row-center'
              onClick={() => setRememberMe(!rememberMe)}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  backgroundColor: '#E6EFF8',
                }}
                className='center'
              >
                {rememberMe && <svg.RememberCheckSvg />}
              </div>
              <span className='t14'>Ingat Saya</span>
            </div>
          </div>
         
          <components.Button
            text="Masuk"
            containerStyle={{ marginBottom: 5 }}
            onClick={async () => {
              if (!name || !password) {
                setErrorMessage("Tolong masukkan Username dan Kata Sandi ");
                return;
              }
              if (/\s/.test(name)) {
                setErrorMessage("Username atau kata sandi salah");
                return;
              }

              try {
                const response = await axiosInstance.post(
                  "/api/login",
                  { name, password, remember: rememberMe },
                  { headers: { "Content-Type": "application/json", "Accept": "application/json, text/html", "client-domain": "security" } }
                );

                const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
                const allPermissions = data?.role?.can || [];
                const patrolPermissions = allPermissions
                  .filter((p: any) => p.name.startsWith("patrol_"))
                  .reduce((acc: any, perm: any) => {
                    acc[perm.name] = {
                      index: perm.can?.index || false,
                      store: perm.can?.store || false,
                      update: perm.can?.update || false,
                      delete: perm.can?.delete || false
                    };
                    return acc;
                  }, {});

                // dispatch(actions.setPermissions(patrolPermissions));
                localStorage.setItem("permissions", JSON.stringify(patrolPermissions));

                const token = data?.access_token;
                if (token) {
                  localStorage.setItem("authToken", token);
                  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
                }

                const patrolGroup = data?.user?.patrol_group || data?.patrol_group;
                  if (patrolGroup) {
                    localStorage.setItem("patrol_group_id", patrolGroup.id);
                    localStorage.setItem("patrol_group_name", patrolGroup.name || `Group ${patrolGroup.id}`);
                    
                    const patrol = data?.user?.patrol || data?.patrol;
                    
                    if (patrol && Array.isArray(patrol)) {
                      const patrolIds = patrol.map(p => p.id);
                      localStorage.setItem("patrol_ids", JSON.stringify(patrolIds));
                      
                      if (patrol.length > 0) {
                        localStorage.setItem("patrol_id", patrol[0].id);
                      }
                    } else if (patrol?.id) {
                      localStorage.setItem("patrol_id", patrol.id);
                      localStorage.setItem("patrol_ids", JSON.stringify([patrol.id]));
                    }
                  }
                localStorage.setItem("rememberMe", rememberMe.toString());
                const userUUID = data?.user?.uuid || data?.user?.id || data?.uuid;
                if (userUUID) {
                  localStorage.setItem("userUUID", userUUID);
                }

                saveCredentialsToSession(name, password, userUUID);

                dispatch(actions.setScreen(TabScreens.Home));
                navigate(Routes.TabNavigator, { replace: true });

              } catch (error: any) {
                if (error.response) {
                  if (error.response.headers["content-type"]?.includes("text/html")) {
                    setErrorMessage("Server error - please contact administrator");
                  } else {
                    setErrorMessage(error.response.data?.message || "Invalid username or password");
                  }
                } else if (error.request) {
                  setErrorMessage("Network error - no response from server");
                } else {
                  setErrorMessage("Request failed: " + error.message);
                }
              }
            }}
          />

          <div
            style={{gap: 4}}
            className='row-center'
          >
            {/* Sign up link can be added here if needed */}
          </div>

        </section>
      </main>
    );
  };

  return (
    <div
      id="screen"
      style={{ opacity }}
    >
      {renderHeader()}
      {renderContent()}
      {errorMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            animation: "fadeIn 0.3s ease-out",
          }}
          onClick={() => setErrorMessage(null)}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "320px",
              width: "85%",
              textAlign: "center",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
              animation: "scaleIn 0.3s ease-out",
              border: "1px solid #E8E8E8",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              marginBottom: "16px",
              display: "flex",
              justifyContent: "center"
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#FFEBEE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#D32F2F" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            
            <h3 style={{ 
              margin: "0 0 12px 0", 
              color: "#D32F2F", 
              fontSize: "18px",
              fontWeight: "600",
            }}>
              Oops!
            </h3>
            
            <div style={{ 
              marginBottom: "20px", 
              color: "#424242", 
              fontSize: "15px",
              lineHeight: "1.4", 
            }}>
              {errorMessage}
            </div>
            
            <div style={{
              height: "4px",
              width: "100%",
              backgroundColor: "#F5F5F5",
              borderRadius: "2px",
              marginBottom: "16px",
              overflow: "hidden"
            }}>
              <div style={{
                height: "100%",
                width: `${(countdown/3) * 100}%`,
                backgroundColor: "#D32F2F",
                transition: "width 1s linear"
              }} />
            </div>
            
            <div style={{ 
              fontSize: "13px", 
              color: "#757575", 
              marginBottom: "16px",
            }}>
              Closing in {countdown} second{countdown !== 1 ? 's' : ''}...
            </div>
            
            <button
              style={{
                backgroundColor: "#D32F2F",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                cursor: "pointer",
                fontWeight: "600",
                fontFamily: "inherit",
                fontSize: "14px",
                width: "100%",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#B71C1C"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#D32F2F"}
              onClick={() => setErrorMessage(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {renderBackground()}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes scaleIn {
            from { 
              opacity: 0;
              transform: scale(0.9); 
            }
            to { 
              opacity: 1;
              transform: scale(1); 
            }
          }
        `}
      </style>
    </div>
  );
};